/**
 * OAuth 2.1 Authorization Code Flow Page
 *
 * Handles OAuth authorization for MCP clients using Google One Tap.
 * This replaces the Cognito hosted UI to provide a consistent UX.
 *
 * Flow:
 * 1. MCP client redirects here with OAuth params (client_id, redirect_uri, etc.)
 * 2. User signs in with Google One Tap
 * 3. We POST to /oauth/authorize with the Google credential
 * 4. Backend generates an authorization code
 * 5. We redirect back to the MCP client with the code
 */

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AuthButton } from '../components/AuthButton';
import './McpAuthorize.css'; // Reuse the same styles

// MCP API URL
const MCP_API_URL = import.meta.env.VITE_MCP_API_URL;

interface OAuthParams {
  client_id: string;
  redirect_uri: string;
  scope: string;
  state: string;
  code_challenge: string;
  code_challenge_method: string;
  callback_url: string;
}

export function OAuthAuthorize() {
  const [searchParams] = useSearchParams();
  const {
    user,
    isLoading: authLoading,
    isAuthenticated,
    idToken,
    accessToken,
    refreshToken,
  } = useAuth();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Parse OAuth parameters from URL
  const oauthParams: OAuthParams = {
    client_id: searchParams.get('client_id') || '',
    redirect_uri: searchParams.get('redirect_uri') || '',
    scope: searchParams.get('scope') || 'openid email profile',
    state: searchParams.get('state') || '',
    code_challenge: searchParams.get('code_challenge') || '',
    code_challenge_method: searchParams.get('code_challenge_method') || 'S256',
    callback_url: searchParams.get('callback_url') || `${MCP_API_URL}/oauth/authorize`,
  };

  // Validate required parameters
  const isValid =
    oauthParams.client_id &&
    oauthParams.redirect_uri &&
    oauthParams.code_challenge &&
    oauthParams.code_challenge_method === 'S256';

  // Auto-authorize when user is authenticated
  useEffect(() => {
    if (isAuthenticated && idToken && isValid && !isSubmitting && !error) {
      handleAuthorize();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, idToken, isValid]);

  // Handle authorization
  const handleAuthorize = async () => {
    if (!isAuthenticated || !idToken) {
      return;
    }

    if (!isValid) {
      setError('Invalid OAuth parameters');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(oauthParams.callback_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          // Send Cognito tokens (already exchanged from Google)
          id_token: idToken,
          access_token: accessToken,
          refresh_token: refreshToken,
          // OAuth parameters
          client_id: oauthParams.client_id,
          redirect_uri: oauthParams.redirect_uri,
          state: oauthParams.state,
          code_challenge: oauthParams.code_challenge,
          code_challenge_method: oauthParams.code_challenge_method,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error_description || data.error || 'Authorization failed');
        setIsSubmitting(false);
        return;
      }

      // Redirect back to MCP client with authorization code
      if (data.redirect_url) {
        window.location.href = data.redirect_url;
      } else {
        setError('No redirect URL returned');
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error('Authorization error:', err);
      setError('Failed to authorize. Please try again.');
      setIsSubmitting(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    // Redirect back with error
    const params = new URLSearchParams({
      error: 'access_denied',
      error_description: 'User denied the authorization request',
    });
    if (oauthParams.state) {
      params.set('state', oauthParams.state);
    }
    window.location.href = `${oauthParams.redirect_uri}?${params.toString()}`;
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="mcp-authorize">
        <div className="mcp-container">
          <div className="mcp-loading">Loading...</div>
        </div>
      </div>
    );
  }

  // Show error for invalid parameters
  if (!isValid) {
    return (
      <div className="mcp-authorize">
        <div className="mcp-container">
          <header className="mcp-header">
            <h1>Authorization Error</h1>
          </header>
          <div className="mcp-error">
            Invalid authorization request. Missing required parameters.
            {!oauthParams.client_id && <div>- client_id is required</div>}
            {!oauthParams.redirect_uri && <div>- redirect_uri is required</div>}
            {!oauthParams.code_challenge && <div>- code_challenge is required (PKCE)</div>}
            {oauthParams.code_challenge_method !== 'S256' && (
              <div>- code_challenge_method must be S256</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Show authorizing state
  if (isSubmitting) {
    return (
      <div className="mcp-authorize">
        <div className="mcp-container">
          <header className="mcp-header">
            <h1>Authorizing...</h1>
            <p className="mcp-subtitle">Please wait while we complete the authorization</p>
          </header>
          <div className="mcp-loading">
            <div className="mcp-spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mcp-authorize">
      <div className="mcp-container">
        <header className="mcp-header">
          <h1>Sign in to HyggeCards</h1>
          <p className="mcp-subtitle">An application is requesting access to your flashcards</p>
        </header>

        {error && <div className="mcp-error">{error}</div>}

        {/* App info */}
        <div className="mcp-app-info">
          <div className="mcp-app-icon">&#128218;</div>
          <div className="mcp-app-details">
            <div className="mcp-app-name">AI Assistant</div>
            <div className="mcp-app-permissions">
              Wants to:
              <ul>
                <li>View your flashcards</li>
                <li>Record your study ratings</li>
                <li>View your categories</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Auth status */}
        <div className="mcp-auth-section">
          {isAuthenticated ? (
            <div className="mcp-user-info">
              <span className="mcp-user-icon">&#128100;</span>
              <span>Signed in as {user?.email}</span>
            </div>
          ) : (
            <div className="mcp-sign-in-prompt">
              <p>Sign in with Google to continue</p>
              <AuthButton />
            </div>
          )}
        </div>

        {/* Action buttons */}
        {isAuthenticated && (
          <div className="mcp-actions">
            <button
              className="mcp-button mcp-button-primary"
              onClick={handleAuthorize}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Authorizing...' : 'Allow'}
            </button>
            <button
              className="mcp-button mcp-button-secondary"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </button>
          </div>
        )}

        {/* Security note */}
        <div className="mcp-security-note">
          <p>
            This will allow the application to access your HyggeCards data. You can revoke
            access at any time by signing out.
          </p>
        </div>
      </div>
    </div>
  );
}
